import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  ContactFieldDto,
  ImportJobDto,
  MondayBoardDto,
  MondayBoardListDto,
  MondayConnectionDto,
  MondaySyncDto,
} from '@carbonwebmail/contracts'
import type { MondayTarget } from '@carbonwebmail/domain'
import { fieldTypeForMondayColumn, mappingProblems } from '@carbonwebmail/domain'
import { useCallback, useEffect, useState } from 'react'

/**
 * Connecting monday, choosing a board, and importing it.
 *
 * Four steps in one view model because they are one errand — connect, pick,
 * map, import — and each one's answer decides what the next one can offer. Four
 * screens would put "which board was I importing" behind a navigation step, the
 * same mistake the contacts board was five screens for.
 *
 * **The mapping starts as a guess and is never a decision made for somebody.**
 * A column called Email fills the address, one called Name fills the name, and
 * a column whose title matches a declared field fills that — everything else is
 * skipped. The same auto-mapping the CSV importer does, for the same reason:
 * the common case should need no work, and every guess is visible and
 * changeable before anything is written.
 */

export type MondayStep = 'connect' | 'board' | 'map' | 'importing' | 'done'

/**
 * How often the screen asks how far the import has got.
 *
 * Two seconds: faster than a page of a hundred items is read and written, and
 * slow enough that an hour-long import is a couple of thousand requests rather
 * than a hundred thousand.
 */
const POLL_MS = 2_000

/** What a board's webhook does before anybody switches anything on. */
const OFF: MondaySyncDto = { onItemCreated: false, onItemDeleted: false }

/**
 * Re-exported rather than restated.
 *
 * There was a copy of this union here, and it drifted the moment the domain
 * grew a kind: the screen could offer "Subscribed or not" and the type said it
 * could not exist. What a monday column may become is a decision the importer
 * acts on, so the domain owns it and this names the same type.
 */
export type { MondayTarget }

export function useMondayImport({ client }: { readonly client: ApiClient }) {
  const [connection, setConnection] = useState<MondayConnectionDto['connection']>(null)
  const [boards, setBoards] = useState<MondayBoardListDto['boards']>([])
  const [board, setBoard] = useState<MondayBoardDto | undefined>(undefined)
  /** The contact columns a monday column can be mapped onto. Read when a board is opened. */
  const [fields, setFields] = useState<readonly ContactFieldDto[]>([])
  const [mapping, setMapping] = useState<Readonly<Record<string, MondayTarget>>>({})
  const [groupsAsSegments, setGroupsAsSegments] = useState(true)
  /** The job this screen is watching, once one has been started. */
  const [jobId, setJobId] = useState<string | undefined>(undefined)
  /** What it last said. Absent until the first answer comes back. */
  const [job, setJob] = useState<ImportJobDto | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  /** Absent until asked for, so the screen does not flash "not connected" first. */
  const [loaded, setLoaded] = useState(false)
  /**
   * Whether the board list came back at all, which an empty `boards` cannot say.
   *
   * "No boards" and "we could not ask" are different facts and the screen says
   * different things about them. Without this it said the confident one.
   */
  const [listed, setListed] = useState(false)
  /**
   * A webhook URL minted after the import, when somebody asked for one.
   *
   * Held here rather than merged into `result` because it is a different fact:
   * the import says whether a URL was minted *by the import*, and this says one
   * was minted since. Both are shown the same way and neither can be shown twice.
   */
  const [webhookUrl, setWebhookUrl] = useState<string | undefined>(undefined)
  /** What the imported board is switched to do. Only meaningful once imported. */
  const [sync, setSync] = useState<MondaySyncDto>(OFF)

  const reload = useCallback(async () => {
    try {
      const found = await client.mondayConnection()
      setConnection(found.connection)
      if (found.connection !== null) {
        setBoards((await client.mondayBoards()).boards)
        setListed(true)
      }
    } catch {
      setListed(false)
      setProblem('We could not reach monday. Try again in a moment.')
    } finally {
      setLoaded(true)
    }
  }, [client])

  useEffect(() => {
    void reload()
  }, [reload])

  const connect = useCallback(
    async (token: string) => {
      setBusy(true)
      setProblem(undefined)
      try {
        const outcome = await client.connectMonday(token)
        if (outcome.status === 'refused') {
          setProblem('monday would not accept that token. Check it was copied whole.')
          return
        }
        setConnection({ account: outcome.account, connectedAt: new Date().toISOString() })
        setBoards((await client.mondayBoards()).boards)
        setListed(true)
      } catch {
        setProblem('We could not reach monday. Try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client],
  )

  const disconnect = useCallback(async () => {
    setBusy(true)
    try {
      await client.disconnectMonday()
      setConnection(null)
      setBoards([])
      setListed(false)
      setBoard(undefined)
      setJobId(undefined)
      setJob(undefined)
    } finally {
      setBusy(false)
    }
  }, [client])

  /**
   * Opens a board and guesses its mapping.
   *
   * The declared fields are read here rather than held, because a column is
   * matched against them by title and the list can have changed since the
   * screen opened.
   */
  const choose = useCallback(
    async (boardId: string) => {
      setBusy(true)
      setProblem(undefined)
      try {
        const found = await client.mondayBoard(boardId)
        if (found === undefined) {
          setProblem('That board is not there any more.')
          return
        }
        const declared = (await client.declaredFields('contacts')).fields
        setFields(declared)
        setBoard(found)
        setMapping(guess(found, declared))
      } catch {
        setProblem('We could not read that board.')
      } finally {
        setBusy(false)
      }
    },
    [client],
  )

  /**
   * Makes a contact column named after a monday column, and maps to it.
   *
   * **Without this the mapper could only offer what already existed**, which on
   * an organisation that has declared nothing is nothing — so a board with a
   * Subscription, a Company name and a Phone offered three answers: skip it,
   * call it the address, or call it the name. Everything else had to be thrown
   * away or declared by hand on another screen first, which is the sort of
   * detour that makes an import not worth doing.
   *
   * **It is made now rather than when the import runs.** The alternative was to
   * carry "a field that does not exist yet" through the mapping, the request and
   * the importer, which is a fourth kind of target and a contract change for
   * something the field list already does. The cost is that abandoning the
   * import leaves a column behind — removable from the contacts board, and
   * visible there rather than hidden.
   *
   * A label already taken is not an error: it means the column somebody wants
   * is already there, so this maps to it.
   */
  const createField = useCallback(
    async (column: { columnId: string; title: string; type: string }) => {
      setBusy(true)
      setProblem(undefined)
      try {
        const outcome = await client.declareField('contacts', {
          label: column.title,
          type: fieldTypeForMondayColumn(column.type),
        })

        if (outcome.status === 'invalid') {
          setProblem(`That column could not be made: ${outcome.problem}`)
          return
        }

        // Either it was just made, or the key was taken and the field it
        // clashed with is the one being asked for. Re-reading covers both and
        // is one request either way.
        const declared = (await client.declaredFields('contacts')).fields
        setFields(declared)

        const made =
          outcome.status === 'declared'
            ? outcome.field
            : declared.find((one) => one.label.trim() === column.title.trim())

        if (made === undefined) {
          setProblem('That column already exists under another name. Pick it from the list.')
          return
        }
        setMapping((current) => ({
          ...current,
          [column.columnId]: { kind: 'field', key: made.key },
        }))
      } catch {
        setProblem('We could not make that column. Try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client],
  )

  const target = useCallback((columnId: string, next: MondayTarget) => {
    setMapping((current) => ({ ...current, [columnId]: next }))
  }, [])

  /**
   * Starts the import, and watches it.
   *
   * **It does not wait for it to finish.** The import reads a board a page at a
   * time in the worker; on Ali's 4,763-item board that was minutes, during
   * which this screen said "Importing…" and nothing else — long enough that
   * somebody reloads, which used to start a second import of the same board.
   *
   * What comes back is a job id. Everything shown afterwards comes from asking
   * how far it has got.
   */
  const run = useCallback(async () => {
    if (board === undefined) return
    setBusy(true)
    setProblem(undefined)
    try {
      const started = await client.importFromMonday({
        boardId: board.boardId,
        mapping,
        groupsAsSegments,
        segmentIds: [],
      })

      if ('refused' in started) {
        setProblem(started.refused)
        return
      }

      setJobId(started.jobId)
    } catch {
      setProblem('We could not start the import. Nothing has been changed — try again.')
    } finally {
      setBusy(false)
    }
  }, [board, client, groupsAsSegments, mapping])

  /**
   * Asks how far it has got, until it stops.
   *
   * Polled rather than streamed: an import takes minutes and reports after each
   * page, so a socket would hold a connection open for something that changes
   * every few seconds. Two seconds is faster than a page is read and slow
   * enough that a long import is a few hundred requests, not a few thousand.
   *
   * The interval is cleared when the job finishes, so a screen left open
   * overnight is not still asking in the morning.
   */
  useEffect(() => {
    if (jobId === undefined) return

    let stopped = false

    const ask = async () => {
      try {
        const found = await client.mondayImportProgress(jobId)
        if (stopped || found === undefined) return

        setJob(found)
        if (found.status === 'done' || found.status === 'failed') {
          stopped = true
          clearInterval(timer)
          // Read once it has finished: the board is only set up by a successful
          // import, so there is nothing to ask about before now.
          if (found.status === 'done' && board !== undefined) {
            setSync((await client.mondaySync(board.boardId)) ?? OFF)
          }
        }
      } catch {
        // A poll that fails is not an import that failed. The next one asks
        // again; saying otherwise would report a network blip as lost work.
      }
    }

    void ask()
    const timer = setInterval(() => void ask(), POLL_MS)
    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [board, client, jobId])

  /**
   * Switching what the webhook does.
   *
   * Applied optimistically and put back if the server refuses: these are two
   * checkboxes and a round trip each would make them feel broken.
   */
  const setSyncAction = useCallback(
    async (next: MondaySyncDto) => {
      if (board === undefined) return
      const previous = sync
      setSync(next)
      try {
        await client.setMondaySync(board.boardId, next)
      } catch {
        setSync(previous)
        setProblem('We could not save that. Try again in a moment.')
      }
    },
    [board, client, sync],
  )

  /**
   * A new webhook URL for the board just imported.
   *
   * Destructive to whatever is already pasted into monday, which is why it is
   * asked for rather than done as part of importing.
   */
  const newWebhook = useCallback(async () => {
    if (board === undefined) return
    setBusy(true)
    setProblem(undefined)
    try {
      setWebhookUrl(await client.newMondayWebhook(board.boardId))
    } catch {
      setProblem('We could not make a new webhook URL. Try again in a moment.')
    } finally {
      setBusy(false)
    }
  }, [board, client])

  const step: MondayStep =
    job?.status === 'done' || job?.status === 'failed'
      ? 'done'
      : jobId !== undefined
        ? 'importing'
        : board !== undefined
          ? 'map'
          : connection === null
            ? 'connect'
            : 'board'

  return {
    step,
    loaded,
    listed,
    connection,
    boards,
    board,
    fields,
    mapping,
    /** Why the mapping cannot be imported with, or nothing. */
    problems: mappingProblems(mapping),
    groupsAsSegments,
    setGroupsAsSegments,
    job,
    sync,
    setSyncAction,
    webhookUrl,
    newWebhook,
    busy,
    problem,
    connect,
    disconnect,
    choose,
    createField,
    target,
    run,
    /** Back to the board list, keeping the connection. */
    again: useCallback(() => {
      setBoard(undefined)
      setJobId(undefined)
      setJob(undefined)
    }, []),
  }
}

/**
 * What each column probably is.
 *
 * Title matching, and deliberately shallow: a column monday calls `email` is
 * the address, and a title that matches a declared field's label or key fills
 * it. Everything else is skipped, because a wrong guess that imports is worse
 * than a blank one somebody has to set.
 */
function guess(
  board: MondayBoardDto,
  declared: readonly { key: string; label: string }[],
): Record<string, MondayTarget> {
  const mapping: Record<string, MondayTarget> = {}
  let hasEmail = false

  for (const column of board.columns) {
    const title = column.title.trim().toLowerCase()

    if (!hasEmail && (column.type === 'email' || title === 'email' || title === 'email address')) {
      mapping[column.columnId] = { kind: 'email' }
      hasEmail = true
      continue
    }
    if (title === 'name' || title === 'full name' || title === 'contact') {
      mapping[column.columnId] = { kind: 'name' }
      continue
    }

    const field = declared.find(
      (one) => one.label.trim().toLowerCase() === title || one.key === title,
    )
    mapping[column.columnId] =
      field === undefined ? { kind: 'skip' } : { kind: 'field', key: field.key }
  }

  return mapping
}
